import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { classifyLoginError, formatThrottleWait, type LoginError } from '../../lib/authErrors';
import { AuthLayout } from './AuthLayout';
import { AuthErrorRegion, AuthSubmit, AuthTextField } from './AuthPrimitives';

const ERROR_REGION_ID = 'forgot-error';

/**
 * "Email me a reset link."
 *
 * THE CONFIRMATION IS THE SAME EITHER WAY. Whether the address has an account,
 * has no password set, or has never existed, this page says one thing — because
 * anything else turns an unauthenticated form into a "who works at Coffman"
 * lookup. The server holds up its end (§A2); this page must not undo it by
 * being helpful about a 404.
 *
 * The only visible failures are the ones that are about the REQUEST rather than
 * the account: throttling and transport.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  useEffect(() => {
    if (secondsLeft === null) return;
    if (secondsLeft <= 0) {
      setError(null);
      setSecondsLeft(null);
      return;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      const classified = classifyLoginError(err);
      setError(classified);
      setSecondsLeft(classified.kind === 'throttled' ? classified.retryAfterSeconds : null);
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout title="Check your email" subtitle="Coffman Excavation · Time & Materials portal">
        <div className="auth-note is-positive">
          If that email has an account, a reset link is on its way. It expires in 60 minutes.
        </div>
        <div className="auth-links">
          <Link className="btn-link" to="/login">
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your email and we'll send you a link to set a new one."
    >
      <form onSubmit={onSubmit}>
        <div className="auth-fields">
          <AuthTextField
            label="Email"
            type="email"
            name="email"
            required
            autoFocus
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            readOnly={submitting}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby={error ? ERROR_REGION_ID : undefined}
            placeholder="you@coffmanteam.com"
          />
        </div>

        {error ? (
          <AuthErrorRegion ref={errorRef} id={ERROR_REGION_ID}>
            {error.kind === 'throttled' ? (
              <>
                Too many requests. Try again in{' '}
                {formatThrottleWait(secondsLeft ?? error.retryAfterSeconds ?? 60)}.
              </>
            ) : (
              error.message
            )}
          </AuthErrorRegion>
        ) : null}

        <AuthSubmit busy={submitting} label="Send reset link" busyLabel="Sending…" />
      </form>

      <div className="auth-links">
        <Link className="btn-link" to="/login">
          Back to sign in
        </Link>
      </div>
    </AuthLayout>
  );
}
